/**
 * Enquête n°30 — L'équipement moto obligatoire 2026 : ce qui change.
 * Article premium hebdomadaire.
 */

import { Lightbulb, ShieldCheck, AlertTriangle, Quote, Users } from 'lucide-react';

export const equipementMoto2026Enquete = {
  slug: 'equipement-moto-2026',
  themeCode: 'EPI',
  issueNumber: 30,
  totalIssues: 31,
  series: 'enquetes',
  category: 'Enquête réglementation 2026',
  shortTitle: 'Équipement 2026',
  title: "Équipement moto 2026 : casque, gants, blouson — ce qui devient obligatoire (et ce qui change)",
  kicker: 'Enquête n°30 · Série « Témoignages & Sécurité »',
  subtitle:
    "Casque ECE 22.06, gants CE certifiés, gilet jaune en cas d'arrêt, dorsale recommandée pour l'ETM 2026... La réglementation s'est durcie. Voici ce qui est obligatoire en 2026, ce qui est fortement recommandé, et ce qui te coûtera 135 € d'amende au prochain contrôle.",
  heroImage: '/img/magazines/epi.jpg',
  heroImageAlt: 'Équipement complet du motard : casque, gants, blouson avec protections',
  readTimeMin: 8,
  seoDescription:
    "Enquête sur l'équipement moto obligatoire 2026 : casque ECE 22.06, gants CE, gilet de visibilité, dorsale. Réglementation R431-1, sanctions, recommandations REMC.",
  seoKeywords:
    'équipement moto obligatoire 2026, ECE 22.06, gants CE moto, gilet jaune moto, dorsale moto, R431-1, sanctions motard, casque homologué',

  edito: {
    title: 'Ce qui te protège, ce qui te sanctionne',
    body: "En France, la loi distingue ce qui est <strong>obligatoire</strong> (sous peine d'amende) de ce qui est <strong>recommandé</strong> (sous peine de blessure grave). Beaucoup de motards ne respectent que la première liste. <strong>Erreur</strong>. La législation 2026 a ajouté des seuils plus stricts pour le casque (ECE 22.06) et les gants (norme CE EN 13594). Cette enquête te donne la check-list complète : ce qui te coûte 135 € en règle, et ce qui te coûte beaucoup plus en cas de chute.",
    signedBy: 'La rédaction Code 2 Roues',
  },

  sections: [
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 1',
      title: "Le casque : ECE 22.06 devient le standard",
      paragraphs: [
        "<strong>R431-1 du Code de la route</strong> : casque obligatoire pour conducteur ET passager. Casque attaché obligatoire (jugulaire serrée). Sanction : <strong>135 € + 3 points</strong>. Le casque doit porter le marquage d'homologation française (NF) ou européen (ECE).",
        "<strong>Nouveauté ECE 22.06</strong> : depuis janvier 2024, les casques neufs vendus doivent être homologués ECE 22.06 (et non plus 22.05). La norme 22.06 ajoute des tests d'impact à plus haute vitesse (jusqu'à 8,2 m/s), des tests latéraux et oblique, et exige une certification de l'écran solaire intégré.",
        "<strong>Les casques 22.05 restent légaux à porter</strong> tant qu'ils sont en bon état (étiquette intacte, pas de choc subi, moins de 5 ans). Mais les vendeurs ne peuvent plus les commercialiser comme neufs. Si tu rachètes un casque d'occasion, vérifie sa date de fabrication.",
        "<strong>Casques avec autocollants ou peinture additionnelle</strong> : ils restent légaux, à condition que le marquage d'homologation reste lisible. Mais attention : certaines peintures attaquent la coque. Vérifie auprès du fabricant.",
      ],
      keyPoints: [
        'Obligatoire conducteur + passager',
        'ECE 22.06 pour casques neufs depuis 2024',
        '22.05 reste légal à porter',
        'Sanction : 135 € + 3 points',
      ],
    },
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 2',
      title: "Les gants CE : obligatoires depuis 2016, contrôlés en 2026",
      paragraphs: [
        "<strong>R431-1-3</strong> : gants homologués CE obligatoires pour conducteur ET passager d'un 2-roues motorisé (depuis le 20 novembre 2016). La norme exigée est <strong>EN 13594</strong> avec étiquetage CE visible.",
        "<strong>Niveaux de protection</strong> : EN 13594 distingue niveau 1 (protection basique) et niveau 2 (renforts paumes, articulations, résistance abrasion supérieure). Pour la circulation urbaine, le niveau 1 suffit légalement. Pour la route ouverte et la piste, niveau 2 recommandé.",
        "<strong>Sanction</strong> : <strong>68 € + 1 point</strong> si tu roules sans gants CE. Les gants de jardinage ou de cuisine NE sont PAS conformes (même s'ils protègent un peu).",
        "<strong>Les mitaines coupées</strong> sont interdites pour la conduite : elles n'offrent aucune protection des doigts, qui sont les premières victimes en cas de chute (réflexe de se rattraper avec les mains).",
      ],
      keyPoints: [
        'Obligatoires depuis 2016',
        'Norme CE EN 13594',
        'Sanction : 68 € + 1 point',
        'Mitaines interdites',
      ],
    },
    {
      type: 'didyouknow',
      icon: Lightbulb,
      title: "L'étude MAIDS : 60 % des blessures sont évitables avec un bon équipement",
      body: "L'étude européenne <strong>MAIDS</strong> (Motorcycle Accidents In Depth Study) a analysé 921 accidents motos en profondeur. Conclusion : <strong>60 % des blessures graves auraient été évitées ou allégées par un équipement complet et adapté</strong> (casque + blouson certifié CE + gants + pantalon protections + bottes). Le gilet airbag, lui, réduit de <strong>74 % le risque de blessure thoracique grave</strong> (étude IFTH 2023). <strong>La loi est un minimum. L'équipement complet est une assurance-vie.</strong>",
    },
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 3',
      title: 'Le gilet de visibilité : obligatoire en cas d\'arrêt forcé',
      paragraphs: [
        "<strong>R416-19</strong> : tout motard doit avoir à bord <strong>un gilet rétro-réfléchissant</strong> (norme EN 1150) à enfiler en cas d'arrêt d'urgence sur la chaussée ou ses dépendances (panne, accident, arrêt forcé).",
        "<strong>Sanction port</strong> : <strong>135 € (4e classe)</strong> si tu sors de ta moto sur la voie publique sans gilet visible. <strong>Sanction absence à bord</strong> : <strong>11 €</strong> (1re classe).",
        "<strong>Astuce</strong> : range-le dans le compartiment sous selle ou dans une sacoche accessible. Pas dans le top-case verrouillé avec une clé que tu pourrais perdre. En cas de chute, tu dois pouvoir l'enfiler en 10 secondes.",
      ],
      keyPoints: [
        "Obligatoire à bord (R416-19)",
        'Port en cas d\'arrêt forcé : 135 €',
        'Absence à bord : 11 €',
        'Accessible en 10 secondes',
      ],
    },
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 4',
      title: 'Le blouson, le pantalon, les bottes : fortement recommandés',
      paragraphs: [
        "<strong>Blouson moto certifié CE EN 17092</strong> : non obligatoire légalement, mais fortement recommandé. Les protections coudes/épaules en mousse haute densité absorbent l'énergie d'impact. Le tissu en cuir ou textile renforcé résiste à l'abrasion (test slider : 4-7 secondes à 100 km/h sur asphalte = aucune brûlure).",
        "<strong>Pantalon moto</strong> : idem, recommandé fortement. Un jean ordinaire se déchire en moins de 0,5 seconde sur l'asphalte à 50 km/h. Un jean Kevlar ou un pantalon textile/cuir certifié CE protège plusieurs secondes.",
        "<strong>Bottes ou chaussures montantes</strong> : recommandé. Les chevilles sont l'articulation la plus fragile à moto (frottement contre le moteur, choc latéral en chute). Des chaussures montantes type rangers ou bottes moto évitent 50 % des fractures de cheville (étude DEKRA).",
        "<strong>Dorsale</strong> : non obligatoire mais REMC la mentionne dans la compétence A2 (équipements de sécurité passive). Une dorsale CE EN 1621-2 niveau 2 réduit de 30 à 60 % le risque de lésion vertébrale grave.",
      ],
      keyPoints: [
        'Blouson CE EN 17092 recommandé',
        'Pantalon Kevlar ou textile',
        'Bottes montantes ou rangers',
        'Dorsale CE EN 1621-2 niveau 2',
      ],
    },
    {
      type: 'trap',
      icon: AlertTriangle,
      title: 'Les pièges du marché low-cost',
      body: "<strong>Attention aux casques 'pas chers' Amazon non marqués CE/ECE</strong> : illégaux et dangereux. Le marquage doit être COUSU à l'intérieur, pas juste imprimé. Idem pour les gants : un autocollant CE collé ≠ certification. Vérifie la présence d'une étiquette tissée. <strong>En cas de doute, achète chez un revendeur agréé</strong> (Dafy, Cardy, Motoblouz, motocycliste équipementier). L'écart de prix avec le marché gris est de 30-50 €, mais ton casque te sauve la vie une fois — pas deux.",
    },
    {
      type: 'tip',
      icon: Users,
      title: 'Check-list 2026 — à imprimer et coller dans ton garage',
      body: "<strong>✅ Casque homologué ECE 22.06 (ou 22.05 valide), jugulaire serrée</strong> · <strong>✅ Gants CE EN 13594</strong> · <strong>✅ Gilet réfléchissant EN 1150 à bord</strong> · <strong>✅ Blouson CE EN 17092 protections coudes/épaules</strong> · <strong>✅ Pantalon Kevlar/textile</strong> · <strong>✅ Bottes ou chaussures montantes</strong> · <strong>✅ Dorsale CE EN 1621-2 niveau 2</strong>. Coût total équipement complet neuf : 600-1 200 €. Coût d'une fracture du fémur sans équipement : 6 mois d'arrêt + séquelles à vie. Le calcul est vite fait.",
    },
  ],

  // QCM contextuel : 8 questions du thème « Équipements de protection » pour valider la compréhension
  quiz: {
    themeCode: 'P',
    questionsCount: 8,
    passingScore: 6,
    title: "QCM : connaître l'équipement obligatoire 2026",
    description: '<strong>8 questions</strong> du thème « Équipements de protection » pour tester ta compréhension. Objectif : <strong>6/8</strong> pour valider.',
  },
};

export default equipementMoto2026Enquete;
